/* Store Locator — Stockist-style controller.
 *
 * Owns:
 *   - locations[]       canonical store data (id, lat, lng, search, DOM card, marker)
 *   - activeId          currently selected store id
 *   - filteredIds       set of ids currently visible (after search filter)
 *
 * UI events route through three public methods:
 *   - setActive(id)     highlights card + marker, pans map, scrolls list
 *   - setFilter(query)  filters list and markers, re-fits bounds, renumbers
 *   - panTo(id)         smooth pan/zoom to a marker
 *
 * Leaflet is loaded via a separate <script> in the section. Because both
 * scripts use `defer`, leaflet.js parses before this one runs. Still, we
 * guard with a small wait to be safe.
 */

class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.store-locs__search');
    this.cardEls = Array.from(this.querySelectorAll('.store-locs__card'));
    this.noResultsEl = this.querySelector('.store-locs__no-results');
    this.mapEl = this.querySelector('.store-locs__map');
    this.viewMapBtn = this.querySelector('[data-view-map]');
    this.dataScript = this.querySelector('script[data-locations]');

    this.locations = this.loadLocations();
    this.activeId = null;
    this.filteredIds = new Set(this.locations.map((l) => l.id));
    this.map = null;

    this.defaults = {
      lat: parseFloat(this.dataset.defaultLat) || -22.5,
      lng: parseFloat(this.dataset.defaultLng) || 145.0,
      zoom: parseInt(this.dataset.defaultZoom, 10) || 5,
    };

    this.renderRichTextHours();
    this.bindEvents();
    this.whenLeafletReady().then(() => this.initMap());
  }

  /* ------------ rich-text fallback ------------
   * Shopify's metaobject rich-text fields don't always auto-render to HTML
   * in Liquid (we see raw JSON like {"type":"root","children":[...]}).
   * Detect that case and convert it to HTML client-side.
   */

  renderRichTextHours() {
    const hoursEls = this.querySelectorAll('.store-locs__hours');
    hoursEls.forEach((el) => {
      const raw = el.textContent.trim();
      if (!raw.startsWith('{') || !raw.includes('"type"')) return;
      // Shopify sometimes outputs metaobject rich-text JSON with literal
      // newlines inside string values, which makes it invalid JSON. Walk the
      // string, escape control chars ONLY inside string literals.
      const sanitized = this.escapeControlCharsInJsonStrings(raw);
      try {
        const tree = JSON.parse(sanitized);
        el.innerHTML = this.richTextNodeToHtml(tree);
      } catch (e) {
        // Leave as-is if not parseable.
      }
    });
  }

  escapeControlCharsInJsonStrings(jsonStr) {
    let out = '';
    let inString = false;
    let escapeNext = false;
    for (let i = 0; i < jsonStr.length; i++) {
      const c = jsonStr[i];
      if (escapeNext) {
        out += c;
        escapeNext = false;
        continue;
      }
      if (c === '\\') {
        out += c;
        escapeNext = true;
        continue;
      }
      if (c === '"') {
        inString = !inString;
        out += c;
        continue;
      }
      if (inString) {
        if (c === '\n') { out += '\\n'; continue; }
        if (c === '\r') { out += '\\r'; continue; }
        if (c === '\t') { out += '\\t'; continue; }
      }
      out += c;
    }
    return out;
  }

  richTextNodeToHtml(node) {
    if (!node || typeof node !== 'object') return '';
    const childHtml = (node.children || []).map((c) => this.richTextNodeToHtml(c)).join('');
    switch (node.type) {
      case 'root':
        return childHtml;
      case 'paragraph':
        return `<p>${childHtml}</p>`;
      case 'heading': {
        const level = Math.min(Math.max(node.level || 2, 1), 6);
        return `<h${level}>${childHtml}</h${level}>`;
      }
      case 'list': {
        const tag = node.listType === 'ordered' ? 'ol' : 'ul';
        return `<${tag}>${childHtml}</${tag}>`;
      }
      case 'list-item':
        return `<li>${childHtml}</li>`;
      case 'link': {
        const url = this.escapeAttr(node.url || '#');
        const title = node.title ? ` title="${this.escapeAttr(node.title)}"` : '';
        const target = node.target ? ` target="${this.escapeAttr(node.target)}" rel="noopener"` : '';
        return `<a href="${url}"${title}${target}>${childHtml}</a>`;
      }
      case 'text': {
        let html = this.escapeHtml(node.value || '').replace(/\n/g, '<br>');
        if (node.bold) html = `<strong>${html}</strong>`;
        if (node.italic) html = `<em>${html}</em>`;
        return html;
      }
      default:
        return childHtml;
    }
  }

  escapeAttr(str) {
    return this.escapeHtml(str).replace(/"/g, '&quot;');
  }

  /* ------------ data ------------ */

  loadLocations() {
    let raw = [];
    if (this.dataScript) {
      try {
        raw = JSON.parse(this.dataScript.textContent) || [];
      } catch (e) {
        console.warn('store-locator: data parse failed', e);
        raw = [];
      }
    }
    return raw
      .map((item) => {
        const cardEl = this.cardEls.find(
          (c) => c.dataset.locationId === item.id
        );
        const lat = parseFloat(item.lat);
        const lng = parseFloat(item.lng);
        return {
          id: item.id,
          name: item.name || '',
          lat: Number.isFinite(lat) ? lat : null,
          lng: Number.isFinite(lng) ? lng : null,
          search: (item.search || '').toLowerCase(),
          cardEl: cardEl || null,
          marker: null,
        };
      })
      .filter((l) => l.cardEl);
  }

  /* ------------ events ------------ */

  bindEvents() {
    if (this.input) {
      this.input.addEventListener('input', this.onSearchInput.bind(this));
    }
    this.cardEls.forEach((card) => {
      card.addEventListener('click', this.onCardClick.bind(this));
      card.addEventListener('keydown', this.onCardKeydown.bind(this));
    });
    if (this.viewMapBtn) {
      this.viewMapBtn.addEventListener('click', this.onViewMapClick.bind(this));
    }
  }

  onSearchInput() {
    this.setFilter(this.input.value);
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 899px)').matches;
  }

  onCardClick(e) {
    // Avoid hijacking clicks on actual links / buttons inside the card.
    if (e.target.closest('a, button, summary')) return;
    const card = e.currentTarget;

    // On mobile the map is hidden — clicking a card jumps straight to
    // Google Maps for that store.
    if (this.isMobileViewport()) {
      const mapUrl = card.dataset.mapUrl;
      if (mapUrl) {
        window.open(mapUrl, '_blank', 'noopener');
        return;
      }
    }

    this.setActive(card.dataset.locationId);
  }

  onCardKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.target.closest('a, button, summary')) return;
      e.preventDefault();
      const card = e.currentTarget;
      if (this.isMobileViewport()) {
        const mapUrl = card.dataset.mapUrl;
        if (mapUrl) {
          window.open(mapUrl, '_blank', 'noopener');
          return;
        }
      }
      this.setActive(card.dataset.locationId);
    }
  }

  onViewMapClick() {
    if (!this.mapEl) return;
    this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ------------ leaflet init ------------ */

  whenLeafletReady() {
    return new Promise((resolve) => {
      if (window.L) return resolve();
      const start = Date.now();
      const check = () => {
        if (window.L) return resolve();
        if (Date.now() - start > 5000) {
          console.warn('store-locator: Leaflet did not load within 5s');
          return resolve();
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  initMap() {
    if (!window.L || !this.mapEl) return;

    const placeholder = this.mapEl.querySelector('.store-locs__map-placeholder');
    if (placeholder) placeholder.remove();

    this.map = window.L.map(this.mapEl, {
      scrollWheelZoom: false,
      attributionControl: true,
    }).setView([this.defaults.lat, this.defaults.lng], this.defaults.zoom);

    window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    // Enable scroll-wheel zoom only after the map is interacted with —
    // prevents accidental zooms while scrolling past the section.
    this.map.once('focus', () => this.map.scrollWheelZoom.enable());
    this.map.on('click', () => this.map.scrollWheelZoom.enable());

    this.buildMarkers();
    this.fitToVisible();
  }

  buildMarkers() {
    if (!this.map) return;
    const L = window.L;
    this.locations.forEach((loc, idx) => {
      if (loc.lat == null || loc.lng == null) return;
      const number = idx + 1;
      const icon = L.divIcon({
        className: 'store-locs__marker-wrapper',
        html: `<div class="store-locs__marker" data-marker-id="${loc.id}">${number}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -16],
      });
      const marker = L.marker([loc.lat, loc.lng], { icon, title: loc.name }).addTo(this.map);
      marker.bindPopup(`<strong>${this.escapeHtml(loc.name)}</strong>`);
      marker.on('click', () => this.setActive(loc.id));
      loc.marker = marker;
    });
  }

  fitToVisible() {
    if (!this.map) return;
    const visibleLocs = this.locations.filter(
      (l) => this.filteredIds.has(l.id) && l.marker
    );
    if (visibleLocs.length === 0) {
      this.map.setView([this.defaults.lat, this.defaults.lng], this.defaults.zoom);
      return;
    }
    if (visibleLocs.length === 1) {
      this.map.setView([visibleLocs[0].lat, visibleLocs[0].lng], 13);
      return;
    }
    const bounds = window.L.latLngBounds(
      visibleLocs.map((l) => [l.lat, l.lng])
    );
    this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }

  /* ------------ public methods (state) ------------ */

  setActive(id) {
    if (this.activeId === id) {
      // Clicking the same card again => re-pan for emphasis but no toggle off.
      this.panTo(id);
      return;
    }
    this.activeId = id;

    this.locations.forEach((loc) => {
      const isActive = loc.id === id;
      if (loc.cardEl) {
        loc.cardEl.classList.toggle('is-active', isActive);
        loc.cardEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        // Auto-expand the schools details on the active card.
        const details = loc.cardEl.querySelector('.store-locs__schools-toggle');
        if (details) details.open = isActive;
      }
      if (loc.marker) {
        const el = loc.marker.getElement();
        if (el) {
          const inner = el.querySelector('.store-locs__marker');
          if (inner) inner.classList.toggle('store-locs__marker--active', isActive);
        }
      }
    });

    const active = this.locations.find((l) => l.id === id);
    if (!active) return;

    // Scroll list card into view.
    if (active.cardEl && typeof active.cardEl.scrollIntoView === 'function') {
      active.cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Pan + open popup on map.
    if (active.marker && this.map) {
      this.panTo(id);
      active.marker.openPopup();
    }
  }

  panTo(id) {
    if (!this.map) return;
    const loc = this.locations.find((l) => l.id === id);
    if (!loc || loc.lat == null || loc.lng == null) return;
    const targetZoom = Math.max(this.map.getZoom(), 13);
    this.map.flyTo([loc.lat, loc.lng], targetZoom, { duration: 0.6 });
  }

  setFilter(query) {
    const q = (query || '').trim().toLowerCase();
    this.filteredIds = new Set();

    let visibleIndex = 0;
    this.locations.forEach((loc) => {
      const match = q === '' || loc.search.includes(q);
      if (match) {
        this.filteredIds.add(loc.id);
        visibleIndex += 1;
        if (loc.cardEl) {
          loc.cardEl.hidden = false;
          // Renumber visible card.
          const num = loc.cardEl.querySelector('.store-locs__num');
          if (num) num.textContent = visibleIndex;
        }
        if (loc.marker) {
          if (!this.map.hasLayer(loc.marker)) this.map.addLayer(loc.marker);
          // Renumber visible marker.
          const el = loc.marker.getElement();
          if (el) {
            const inner = el.querySelector('.store-locs__marker');
            if (inner) inner.textContent = visibleIndex;
          }
        }
      } else {
        if (loc.cardEl) loc.cardEl.hidden = true;
        if (loc.marker && this.map && this.map.hasLayer(loc.marker)) {
          this.map.removeLayer(loc.marker);
        }
      }
    });

    if (this.noResultsEl) {
      this.noResultsEl.hidden = !(q !== '' && this.filteredIds.size === 0);
    }

    this.fitToVisible();
  }

  /* ------------ util ------------ */

  escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  disconnectedCallback() {
    if (this.map) {
      try { this.map.remove(); } catch (e) {}
      this.map = null;
    }
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}

// Theme-editor live-reload support: re-init the section when Shopify
// re-renders it. Custom elements re-construct automatically when the DOM
// is replaced, so we just need to make sure old map instances are torn
// down (handled in disconnectedCallback).
document.addEventListener('shopify:section:load', (e) => {
  const el = e.target.querySelector('store-locator');
  if (el && !el.map) {
    // Force a re-init by re-constructing.
    customElements.upgrade(el);
  }
});
